import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Button from "../components/common/Button";
import Lottie from "lottie-react";
import figureAnimation from "../assets/animations/Doctor.json";
import {
  Clock,
  UserCheck,
  FileText,
  ChevronRight,
  User,
  X,
  BadgeQuestionMark,
  Settings2,
} from "lucide-react";
import useQuestionnaireStore from "../store/useQuestionnaireStore";

// Feature Data - Positions are now calculated automatically!
// You can add more items here, and they will auto-adjust.
const features = [
  {
    id: 1,
    label: "How It Works",
    icon: <Settings2 size={24} />,
    content: (
      <div className="how-it-works">
        <div className="how-step">
          <h4>1. AI Risk Assessment</h4>
          <p>
            Answer 10 clinical questions to identify your mechanical pain
            pattern instantly.
          </p>
        </div>

        <div className="how-step">
          <h4>2. Physio Safety Triage</h4>
          <p>
            A certified Physio reviews your data via a 15‑minute video call to
            rule out red flags.
          </p>
        </div>

        <div className="how-step">
          <h4>3. Precision Rehab</h4>
          <p>
            Unlock your customized 6‑week video protocol. 100% guided. 100%
            safe.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 2,
    label: "What Problem We Solve",
    icon: <User size={24} />,
    content: (
      <div className="how-it-works">
        <div className="how-step">
          <h4>🪑 The "Sitter's Back"</h4>
          <p>
            Throbbing low back pain after 2 hours of sitting? Your discs are
            likely intolerant to flexion. We fix this with Extension Protocols.
          </p>
        </div>

        <div className="how-step">
          <h4>💻 Tech Neck & Migraines</h4>
          <p>
            Chin poking forward at the screen? This shuts down deep neck
            flexors. We fix this with Deep Activation.
          </p>
        </div>

        <div className="how-step">
          <h4>⚡Sciatica & Nerve Pain</h4>
          <p>
            Shooting pain down the leg? Don't just stretch. We use Neurodynamic
            to floss the nerve safely.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 3,
    label: "Why Choose Us ?",
    icon: <BadgeQuestionMark size={24} />,
    content: (
      <div className="how-it-works">
        <div className="how-step">
          <h4>Built by Physios, Not Just Code</h4>
          <p>
            I treated 1,000+ patients in the clinic and saw the same pattern:
            Sitting is the new smoking. Most apps just give you generic yoga. I
            built The Next Move to bring clinical-grade, evidence-based rehab
            directly to your laptop.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 4,
    label: "Convenient & Flexible",
    icon: <Clock size={24} />,
    content:
      "Flexible sessions that fit your schedule perfectly—no need for travel, long waits, or rearranging your day.",
  },
  {
    id: 5,
    label: "Personalized Programs",
    icon: <UserCheck size={24} />,
    content:
      "Tailored to your condition, goals, and progress to ensure effective, personalized care.",
  },
  {
    id: 6,
    label: "Evidence-Based Approach",
    icon: <FileText size={24} />,
    content:
      "Our methods are based on the latest physiotherapy research, providing effective, evidence-based care.",
  },
];

const Introduction = () => {
  const navigate = useNavigate();
  const resetStore = useQuestionnaireStore((state) => state.resetStore);
  const [selectedFeature, setSelectedFeature] = useState(null);

  const handleProceed = () => {
    resetStore();
    navigate("/step-1");
  };

  return (
    <motion.div
      className="page introduction-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="intro-header">
        <motion.h2
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          The Next Move: <span className="highlight">Clinical AI Rehab</span>
        </motion.h2>
        <motion.p
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          AI assisted assessment + 1 on 1 physio verification Get your clinical
          risk score in 3 minutes
        </motion.p>
      </div>

      {/* Main Orbit Container */}
      <div className="orbit-container">
        {/* Central Figure */}
        <div className="center-figure-wrapper">
          <motion.div
            className="lottie-circle"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <Lottie
              animationData={figureAnimation}
              loop={true}
              className="doctor-anim"
            />
          </motion.div>

          <motion.div
            className="cta-wrapper"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <Button onClick={handleProceed} className="start-btn">
              Start Free AI Check <ChevronRight size={18} />
            </Button>
          </motion.div>
        </div>

        {/* Floating Bubbles Layer */}
        <div className="bubbles-layer">
          {features.map((feature, index) => {
            const total = features.length;
            const radius = 36; // percent of the wrapper — reduce to bring bubbles closer
            const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
            const left = 50 + radius * Math.cos(angle);
            const top = 40 + radius * Math.sin(angle);

            return (
              <FeatureBubble
                key={feature.id}
                feature={feature}
                onClick={() => setSelectedFeature(feature)}
                index={index}
                position={{ left: `${left}%`, top: `${top}%` }}
              />
            );
          })}
        </div>
      </div>

      {/* --- FEATURE MODAL POPOVER --- */}
      <AnimatePresence>
        {selectedFeature && (
          <motion.div
            className="intro-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedFeature(null)}
          >
            <motion.div
              className="intro-modal-content"
              initial={{ scale: 0.8, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="close-btn"
                onClick={() => setSelectedFeature(null)}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>

              <div className="modal-icon-wrapper">{selectedFeature.icon}</div>

              <h3>{selectedFeature.label}</h3>
              <div className="modal-body">{selectedFeature.content}</div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Sub-component for individual bubbles
const FeatureBubble = ({ feature, onClick, index, position }) => {
  const floatDuration = 3 + index * 0.5;

  return (
    <div
      className={`feature-bubble-wrapper bubble-${index}`}
      // outer wrapper used only for absolute placement and centering
      style={{
        ...position,
        transform: "translate(-50%, -50%)",
        cursor: "pointer",
      }}
      onClick={onClick}
    >
      {/* inner motion layer handles the floating animation so the translate(-50%,-50%) centering is preserved */}
      <motion.div
        initial={{ y: 0, x: 0 }}
        animate={{ y: [0, -10, 0], x: [0, 6, 0] }}
        transition={{
          duration: floatDuration,
          repeat: Infinity,
          repeatType: "mirror",
          ease: "easeInOut",
          delay: index * 0.12,
        }}
        style={{ willChange: "transform" }}
      >
        <motion.div
          className="bubble-circle"
          whileHover={{ scale: 1.12 }}
          whileTap={{ scale: 0.96 }}
          transition={{ type: "spring", stiffness: 300, damping: 22 }}
        >
          <span className="icon">{feature.icon}</span>
          <span className="label">{feature.label}</span>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Introduction;
